import React, { useState, useEffect } from 'react';
import { DEFAULT_ALLOWED_MODEL_FOR_PUBLIC_FUNCTIONS } from '../config/modelConfig';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Paper,
  Tooltip,
  Alert,
  Snackbar,
  Autocomplete,
  Chip,
  Stack,
  Link,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { fetchWithPublicMode, getPublicModeParams } from '../utils/apiUtils';
import { DeleteConfirmationModal, type DependentItem } from './common';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Info as InfoIcon,
  Code as CodeIcon,
  AutoAwesome as AutoAwesomeIcon,
  Close as CloseIcon,
  DataObject as ObjectIcon,
  Edit as EditIcon,
  Lock as LockIcon,
  ArrowBack as ArrowBackIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import CircularProgress from '@mui/material/CircularProgress';
import { DataTable, type Column, type SortDirection } from '../components/common';
import { DraftObjectSchemaView } from './schema/ObjectSchemaView';
import { ObjectJsonTooltip } from './schema/ObjectJsonTooltip';

// Field types matching your backend
enum FieldType {
  STRING = 'string',
  INTEGER = 'integer',
  FLOAT = 'float',
  BOOLEAN = 'boolean',
  DATE = 'date',
  DATETIME = 'datetime',
  JSON = 'json',
  TEXT = 'text',
  UUID = 'uuid',
  ARRAY = 'array',
  REFERENCE = 'reference',
}

interface Field {
  id: string;
  name: string;
  display_name: string;
  field_type: FieldType;
  description?: string;
  is_required: boolean;
  is_unique: boolean;
  is_indexed: boolean;
  min_length?: number;
  max_length?: number;
  min_value?: number;
  max_value?: number;
  regex_pattern?: string;
  allowed_values?: string[];
  default_value?: any;
  ui_widget?: string;
  help_text?: string;
  placeholder?: string;
  reference_object_id?: string;
  array_item_type?: FieldType;
  nested_fields?: Field[];  // For JSON objects with nested structure
}

interface CustomObject {
  id?: string;
  name: string;
  display_name: string;
  description?: string;
  is_searchable: boolean;
  fields?: Field[];
  field_count?: number;
  customer_id?: string;
  version_count?: number;
}

interface ObjectBuilderComponentProps {
  isModal?: boolean;
  onSave?: (object: CustomObject) => void;
  onClose?: () => void;
  initialObjectId?: string | null;
}

const fieldTypeOptions = [
  { value: FieldType.STRING, label: 'Text', icon: '📝' },
  { value: FieldType.INTEGER, label: 'Number', icon: '🔢' },
  { value: FieldType.FLOAT, label: 'Decimal', icon: '🔢' },
  { value: FieldType.BOOLEAN, label: 'Yes/No', icon: '✅' },
  { value: FieldType.DATE, label: 'Date', icon: '📅' },
  { value: FieldType.DATETIME, label: 'Date & Time', icon: '🕐' },
  { value: FieldType.JSON, label: 'JSON Object', icon: '{}' },
  { value: FieldType.TEXT, label: 'Long Text', icon: '📄' },
  { value: FieldType.UUID, label: 'Unique ID', icon: '🆔' },
  { value: FieldType.ARRAY, label: 'List', icon: '📋' },
  { value: FieldType.REFERENCE, label: 'Link to Object', icon: '🔗' },
];

/**
 * Utility function to limit concurrent async operations
 * Prevents overwhelming the backend with too many simultaneous requests
 * @param tasks Array of async functions to execute
 * @param maxConcurrent Maximum number of concurrent operations (default: 5)
 */
async function withConcurrencyLimit<T>(
  tasks: (() => Promise<T>)[],
  maxConcurrent: number = 5
): Promise<T[]> {
  const results: Promise<T>[] = [];
  const executing: Promise<T>[] = [];
  
  for (const task of tasks) {
    const promise = task().then(result => {
      const index = executing.indexOf(promise);
      if (index !== -1) {
        executing.splice(index, 1);
      }
      return result;
    });
    
    results.push(promise);
    executing.push(promise);
    
    if (executing.length >= maxConcurrent) {
      await Promise.race(executing);
    }
  }
  
  return Promise.all(results);
}

export const ObjectBuilderComponent: React.FC<ObjectBuilderComponentProps> = ({ 
  isModal = false, 
  onSave, 
  onClose,
  initialObjectId 
}) => {
  const { user, permissions, isPublicMode, hasActiveSubscription } = useAuth();
  const navigate = useNavigate();
  const [object, setObject] = useState<CustomObject>({
    name: '',
    display_name: '',
    description: '',
    is_searchable: true,
    fields: [],
  });
  
  const [fields, setFields] = useState<Field[]>([]);
  const [showAdvanced, setShowAdvanced] = useState<{ [key: string]: boolean }>({});
  const [savedObjects, setSavedObjects] = useState<CustomObject[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [cascadeToFunctions, setCascadeToFunctions] = useState(false);
  // const [showImportDialog, setShowImportDialog] = useState(false);
  // const [importJson, setImportJson] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'warning' | 'info' });
  const [loading, setLoading] = useState(false);
  const [selectedObjectId, setSelectedObjectId] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [objectToDelete, setObjectToDelete] = useState<{ id: string; name: string } | null>(null);
  const [dependentFunctions, setDependentFunctions] = useState<DependentItem[]>([]);
  const [quickStartInput, setQuickStartInput] = useState('');
  const [quickStartLoading, setQuickStartLoading] = useState(false);
  const [quickStartInputType, setQuickStartInputType] = useState<'text' | 'image'>('text');
  const [objectUsage, setObjectUsage] = useState<Record<string, { count: number; functions: { id: string; name: string }[] }>>({});
  const [objectVersionUsage, setObjectVersionUsage] = useState<Record<string, any>>({});
  // const [quickStartExpanded, setQuickStartExpanded] = useState(false);
  const [creationMode, setCreationMode] = useState<'ai' | 'manual' | null>(null);
  
  // Table state
  const [tablePage, setTablePage] = useState(0);
  const [tableRowsPerPage, setTableRowsPerPage] = useState(10);
  const [tableSortBy, setTableSortBy] = useState<string>('created_at');
  const [tableSortDir, setTableSortDir] = useState<SortDirection>('desc');
  const [tableSearchTerm, setTableSearchTerm] = useState('');
  const [objectsLoaded, setObjectsLoaded] = useState(false);
  
  // Versioning state
  const [objectVersions, setObjectVersions] = useState<any[]>([]);
  const [currentVersion, setCurrentVersion] = useState<number | null>(null);

  // What the selected object looked like when loaded or saved, to tell whether the preview
  // can show its runtime schema or must be built from unsaved edits.
  const savedDefinitionRef = React.useRef<{ id: string; key: string } | null>(null);
  const definitionKey = () => JSON.stringify([object.display_name, object.description, fields]);
  useEffect(() => {
    savedDefinitionRef.current = selectedObjectId ? { id: selectedObjectId, key: definitionKey() } : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedObjectId]);

  // Load saved objects on mount
  useEffect(() => {
    if (user) {
      loadSavedObjects();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Load specific object if initialObjectId is provided
  useEffect(() => {
    if (initialObjectId && savedObjects.length > 0) {
      const objectToEdit = savedObjects.find(obj => obj.id === initialObjectId);
      if (objectToEdit) {
        // Set creation mode to manual to show the form
        setCreationMode('manual');
        handleSelectObject(initialObjectId);
        // Scroll to top to show the form
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialObjectId, savedObjects]);

  const loadSavedObjects = async () => {
    try {
      const idToken = await user?.getIdToken();
      const isAdmin = permissions?.isAdmin || false;
      
      // Get appropriate parameters based on public mode
      const params = new URLSearchParams({
        ...getPublicModeParams(isPublicMode, isAdmin),
        // Fetch all objects without pagination (up to 100 per page) for reference dropdown
        // This ensures users can link to any object when creating reference fields
        page_size: '100'
      });
      
      const response = await fetchWithPublicMode(
        `/api/v2/custom-objects/?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${idToken}`
          }
        },
        isPublicMode,
        isAdmin
      );
      const data = await response.json();
      setObjectsLoaded(true);
      if (data && data.results && Array.isArray(data.results)) {
        setSavedObjects(data.results);
        
        // Fetch usage data for each object (skip public objects)
        // Use concurrency limiting to prevent overwhelming the database connection pool
        const usageData: Record<string, { count: number; functions: { id: string; name: string }[] }> = {};
        const usageTasks = data.results
          .filter((obj: CustomObject) => obj.id && obj.customer_id !== 'public')
          .map((obj: CustomObject) => {
            const objId = obj.id as string; // Ensure id is string after filter
            return async () => {
              try {
                const usageRes = await fetch(`/api/v2/custom-objects/${objId}/usage`, {
                  headers: {
                    'Authorization': `Bearer ${idToken}`,
                  },
                });
                if (usageRes.ok) {
                  const usage = await usageRes.json();
                  const usageInfo = usage.data || usage;
                  usageData[objId] = {
                    count: usageInfo.function_count || 0,
                    functions: usageInfo.functions || [],
                  };
                } else {
                  // If not OK (404 etc), default to no usage
                  usageData[objId] = {
                    count: 0,
                    functions: [],
                  };
                }
              } catch (err) {

                // Default to no usage on error
                usageData[objId] = {
                  count: 0,
                  functions: [],
                };
              }
            };
          });
        
        // Execute with max 5 concurrent requests
        await withConcurrencyLimit(usageTasks, 5);
        setObjectUsage(usageData);
      } else {
        setSavedObjects([]);
        setObjectUsage({});
      }
    } catch (error) {
      setObjectsLoaded(true);
      setSavedObjects([]);
      setObjectUsage({});
    }
  };

  const loadObjectVersions = async (objectId: string) => {
    try {
      const idToken = await user?.getIdToken();
      const response = await fetch(`/api/v2/custom-objects/${objectId}/versions`, {
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        const versions = data?.data || data?.results || [];
        setObjectVersions(versions);
      }
    } catch (error) {

    }
  };

  const handleSelectObject = async (objectId: string) => {
    try {
      const idToken = await user?.getIdToken();
      const response = await fetchWithPublicMode(`/api/v2/custom-objects/${objectId}`, {
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      }, isPublicMode, permissions?.isAdmin || false);
      if (!response.ok) {
        throw new Error('Failed to fetch object details');
      }
      const data = await response.json();
      let fullObject = data?.results || data?.data || data?.item || data;
      
      // If fields are not included or empty, fetch them separately
      if (fullObject && (!fullObject.fields || fullObject.fields.length === 0)) {
        const fieldsResponse = await fetchWithPublicMode(
          `/api/v2/custom-objects/${objectId}/fields`,
          {
            headers: {
              'Authorization': `Bearer ${idToken}`
            }
          },
          isPublicMode,
          permissions?.isAdmin || false
        );
        
        if (fieldsResponse.ok) {
          const fieldsData = await fieldsResponse.json();
          fullObject.fields = fieldsData?.data || fieldsData?.results || [];
        }
      }
      if (fullObject && fullObject.id) {
        setObject({
          id: fullObject.id,
          name: fullObject.name,
          display_name: fullObject.display_name,
          description: fullObject.description,
          is_searchable: Boolean(fullObject.is_searchable),
        });
        // Recursive function to map fields including nested fields
        const mapFieldFromAPI = (f: any): Field => ({
          id: String(f.id),
          name: f.name,
          display_name: f.display_name,
          field_type: f.field_type,
          description: f.description ?? f.help_text ?? '',
          is_required: Boolean(f.is_required),
          is_unique: Boolean(f.is_unique),
          is_indexed: Boolean(f.is_indexed),
          min_length: f.min_length ?? undefined,
          max_length: f.max_length ?? undefined,
          min_value: f.min_value ?? undefined,
          max_value: f.max_value ?? undefined,
          regex_pattern: f.regex_pattern ?? undefined,
          allowed_values: f.allowed_values ?? undefined,
          default_value: f.default_value ?? undefined,
          ui_widget: f.ui_widget ?? undefined,
          help_text: f.help_text ?? undefined,
          placeholder: f.placeholder ?? undefined,
          reference_object_id: f.reference_object_id ?? undefined,
          array_item_type: f.array_item_type ?? undefined,
          nested_fields: f.nested_fields ? f.nested_fields.map(mapFieldFromAPI) : undefined,
        });
        
        const loadedFields: Field[] = Array.isArray(fullObject.fields)
          ? fullObject.fields.map(mapFieldFromAPI)
          : [];
        setFields(loadedFields);
        setSelectedObjectId(fullObject.id);
        setCurrentVersion(fullObject.version || 1);
        
        // Load versions for this object
        await loadObjectVersions(fullObject.id);
      } else {
        throw new Error('Unexpected object details response');
      }
    } catch (error) {

      setSnackbar({ open: true, message: 'Failed to load object details', severity: 'error' });
    }
  };

  const addField = () => {
    const newField: Field = {
      id: `field_${Date.now()}`,
      name: '',
      display_name: '',
      field_type: FieldType.STRING,
      description: '',
      is_required: false,
      is_unique: false,
      is_indexed: false,
      help_text: '',
      placeholder: '',
    };
    setFields([...fields, newField]);
  };

  const updateField = (id: string, updates: Partial<Field>, parentField?: Field) => {
    if (parentField) {
      // Update nested field
      const updateNestedFields = (fields: Field[]): Field[] => {
        return fields.map(f => {
          if (f.id === parentField.id) {
            return {
              ...f,
              nested_fields: f.nested_fields?.map(nf => 
                nf.id === id ? { ...nf, ...updates } : nf
              )
            };
          } else if (f.nested_fields) {
            return {
              ...f,
              nested_fields: updateNestedFields(f.nested_fields)
            };
          }
          return f;
        });
      };
      setFields(updateNestedFields(fields));
    } else {
      setFields(fields.map(f => f.id === id ? { ...f, ...updates } : f));
    }
  };

  const removeField = (id: string, parentField?: Field) => {
    if (parentField) {
      // Remove nested field
      const removeNestedField = (fields: Field[]): Field[] => {
        return fields.map(f => {
          if (f.id === parentField.id) {
            return {
              ...f,
              nested_fields: f.nested_fields?.filter(nf => nf.id !== id)
            };
          } else if (f.nested_fields) {
            return {
              ...f,
              nested_fields: removeNestedField(f.nested_fields)
            };
          }
          return f;
        });
      };
      setFields(removeNestedField(fields));
    } else {
      setFields(fields.filter(f => f.id !== id));
    }
  };
  
  const addNestedField = (parentField: Field) => {
    const newField: Field = {
      id: `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: 'new_field',
      display_name: 'New Field',
      field_type: FieldType.STRING,
      description: '',
      is_required: false,
      is_unique: false,
      is_indexed: false,
      help_text: '',
      placeholder: '',
    };
    
    updateField(parentField.id, {
      nested_fields: [...(parentField.nested_fields || []), newField]
    });
  };

  const generateFieldName = (displayName: string): string => {
    return displayName
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '');
  };

  const generateDescription = (field: Field): string => {
    const descriptions: Record<FieldType, (field: Field) => string> = {
      [FieldType.STRING]: (f) => `Text field for ${f.display_name || 'text data'}. The AI should extract textual information that represents ${f.display_name}.`,
      [FieldType.INTEGER]: (f) => `Whole number field for ${f.display_name || 'numeric data'}. The AI should extract numeric values without decimals.`,
      [FieldType.FLOAT]: (f) => `Decimal number field for ${f.display_name || 'numeric data'}. The AI should extract precise numeric values with decimals.`,
      [FieldType.BOOLEAN]: (f) => `Yes/No field for ${f.display_name || 'boolean data'}. The AI should determine if this condition is true or false.`,
      [FieldType.DATE]: (f) => `Date field for ${f.display_name || 'date information'}. The AI should extract dates in YYYY-MM-DD format.`,
      [FieldType.DATETIME]: (f) => `Date and time field for ${f.display_name || 'timestamp data'}. The AI should extract full timestamps.`,
      [FieldType.JSON]: (f) => `Structured data field for ${f.display_name || 'complex data'}. The AI should extract nested or complex information.`,
      [FieldType.TEXT]: (f) => `Long text field for ${f.display_name || 'detailed content'}. The AI should extract extended textual content.`,
      [FieldType.UUID]: (f) => `Unique identifier field for ${f.display_name || 'ID data'}. The AI should extract or generate unique identifiers.`,
      [FieldType.ARRAY]: (f) => `List field for multiple ${f.display_name || 'items'}. The AI should extract multiple values as an array.`,
      [FieldType.REFERENCE]: (f) => `Reference field linking to ${f.display_name || 'another object'}. The AI should extract identifiers that reference other objects.`,
    };
    
    return descriptions[field.field_type]?.(field) || `Field for ${field.display_name}`;
  };

  const renderFieldBuilder = (field: Field, index: number, depth: number = 0, parentField?: Field) => {
    const isAdvancedOpen = showAdvanced[field.id] || false;

    return (
      <Card key={field.id} sx={{ 
        mb: 2, 
        ml: depth * 4, // Indent nested fields
        border: depth === 0 ? '1px solid #e0e0e0' : '1px solid #f0f0f0',
        backgroundColor: depth > 0 ? '#fafafa' : 'white'
      }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Field Name"
                placeholder="e.g., Customer Name"
                value={field.display_name}
                onChange={(e) => {
                  const displayName = e.target.value;
                  updateField(field.id, {
                    display_name: displayName,
                    name: generateFieldName(displayName),
                  }, parentField);
                }}
                helperText={field.name && `Will be saved as: ${field.name}`}
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Field Type</InputLabel>
                <Select
                  value={field.field_type}
                  onChange={(e) => updateField(field.id, { field_type: e.target.value as FieldType }, parentField)}
                  label="Field Type"
                >
                  {fieldTypeOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>{option.icon}</span>
                        <span>{option.label}</span>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            {/* Show array item type immediately if List is selected */}
            {field.field_type === FieldType.ARRAY && (
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>List contains</InputLabel>
                  <Select
                    value={field.array_item_type || FieldType.STRING}
                    onChange={(e) => updateField(field.id, { array_item_type: e.target.value as FieldType }, parentField)}
                    label="List contains"
                  >
                    <MenuItem value={FieldType.STRING}>Strings</MenuItem>
                    <MenuItem value={FieldType.INTEGER}>Integers</MenuItem>
                    <MenuItem value={FieldType.FLOAT}>Floats</MenuItem>
                    <MenuItem value={FieldType.BOOLEAN}>Booleans</MenuItem>
                    <MenuItem value={FieldType.DATE}>Dates</MenuItem>
                    <MenuItem value={FieldType.DATETIME}>DateTimes</MenuItem>
                    <MenuItem value={FieldType.JSON}>Custom Object</MenuItem>
                    <MenuItem value={FieldType.UUID}>UUIDs</MenuItem>
                    <MenuItem value={FieldType.REFERENCE}>Link to Existing Object</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
            
            {/* Show object selector for List of References */}
            {field.field_type === FieldType.ARRAY && field.array_item_type === FieldType.REFERENCE && (
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Object Type</InputLabel>
                  <Select
                    value={field.reference_object_id || ''}
                    onChange={(e) => updateField(field.id, { reference_object_id: e.target.value }, parentField)}
                    label="Object Type"
                  >
                    <MenuItem value="">
                      <em>Select object...</em>
                    </MenuItem>
                    {savedObjects
                      // .filter(obj => obj.customer_id !== 'public')
                      .map(obj => (
                        <MenuItem key={obj.id} value={obj.id}>
                          {obj.display_name || obj.name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            
            {/* Show reference object selection if Link to Object is selected */}
            {field.field_type === FieldType.REFERENCE && (
              <Grid item xs={12} md={3}>
                <Autocomplete
                  disablePortal
                  options={savedObjects}
                  getOptionLabel={(option) => option.display_name || option.name || ''}
                  value={savedObjects.find(obj => obj.id === field.reference_object_id) || null}
                  onChange={(_, value) => updateField(field.id, { reference_object_id: value?.id }, parentField)}
                  renderInput={(params) => <TextField {...params} label="Links to" placeholder="Search objects..." />}
                  size="small"
                  fullWidth
                  ListboxProps={{
                    style: {
                      maxHeight: '300px',
                    }
                  }}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box>
                        <Typography variant="body2">{option.display_name || option.name}</Typography>
                        {option.description && (
                          <Typography variant="caption" color="textSecondary" display="block">
                            {option.description}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  )}
                />
              </Grid>
            )}
            
            <Grid item xs={12} md={
              field.field_type === FieldType.ARRAY && field.array_item_type === FieldType.REFERENCE ? 1 :
              field.field_type === FieldType.ARRAY || field.field_type === FieldType.REFERENCE ? 2 : 4
            }>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.is_required}
                      onChange={(e) => updateField(field.id, { is_required: e.target.checked }, parentField)}
                    />
                  }
                  label="Required"
                />
                {/* Hidden for now - all fields are searchable by default
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.is_indexed}
                      onChange={(e) => updateField(field.id, { is_indexed: e.target.checked }, parentField)}
                    />
                  }
                  label="Searchable"
                />
                */}
                <Tooltip title="Delete field">
                  <IconButton onClick={() => removeField(field.id, parentField)} color="error">
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="AI Extraction Instructions"
                placeholder="Describe what the AI should look for and extract for this field..."
                value={field.help_text || ''}
                onChange={(e) => updateField(field.id, { help_text: e.target.value }, parentField)}
                helperText={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InfoIcon fontSize="small" />
                    <span>Suggested: {generateDescription(field)}</span>
                  </Box>
                }
              />
            </Grid>
          </Grid>


          <Accordion 
            expanded={isAdvancedOpen}
            onChange={() => setShowAdvanced({ ...showAdvanced, [field.id]: !isAdvancedOpen })}
            sx={{ mt: 2, backgroundColor: '#f5f5f5' }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2" color="textSecondary">
                Advanced Options
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                {/* Validation Options */}
                {(field.field_type === FieldType.STRING || field.field_type === FieldType.TEXT) && (
                  <>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Min Length"
                        value={field.min_length || ''}
                        onChange={(e) => updateField(field.id, { min_length: parseInt(e.target.value) || undefined }, parentField)}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Max Length"
                        value={field.max_length || ''}
                        onChange={(e) => updateField(field.id, { max_length: parseInt(e.target.value) || undefined }, parentField)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Regex Pattern"
                        placeholder="e.g., ^[A-Z][a-z]+$"
                        value={field.regex_pattern || ''}
                        onChange={(e) => updateField(field.id, { regex_pattern: e.target.value }, parentField)}
                        helperText="Regular expression for validation"
                      />
                    </Grid>
                  </>
                )}
                
                {(field.field_type === FieldType.INTEGER || field.field_type === FieldType.FLOAT) && (
                  <>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Min Value"
                        value={field.min_value || ''}
                        onChange={(e) => updateField(field.id, { min_value: parseFloat(e.target.value) || undefined }, parentField)}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Max Value"
                        value={field.max_value || ''}
                        onChange={(e) => updateField(field.id, { max_value: parseFloat(e.target.value) || undefined }, parentField)}
                      />
                    </Grid>
                  </>
                )}
                

                {/* Additional advanced options... */}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.is_unique}
                        onChange={(e) => updateField(field.id, { is_unique: e.target.checked }, parentField)}
                      />
                    }
                    label="Values must be unique"
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </CardContent>
        
        {/* Render nested fields recursively for JSON objects and Lists of Custom Objects */}
        {(field.field_type === FieldType.JSON || 
          (field.field_type === FieldType.ARRAY && field.array_item_type === FieldType.JSON)) && (
          <Box sx={{ p: 2, pt: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, ml: 2 }}>
              <Typography variant="subtitle2" color="primary" sx={{ flex: 1 }}>
                {field.field_type === FieldType.ARRAY ? 'Object Structure:' : 'Nested Fields:'}
              </Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => addNestedField(field)}
              >
                Add Field
              </Button>
            </Box>
            {field.nested_fields && field.nested_fields.length > 0 ? (
              field.nested_fields.map((nestedField, idx) => 
                renderFieldBuilder(nestedField, idx, depth + 1, field)
              )
            ) : (
              <Typography variant="body2" color="textSecondary" sx={{ ml: 2, fontStyle: 'italic' }}>
                No fields defined. Click "Add Field" to define the {field.field_type === FieldType.ARRAY ? 'object' : 'nested'} structure.
              </Typography>
            )}
          </Box>
        )}
      </Card>
    );
  };

  // Simplified conversion functions (keeping the essential logic)
  const convertSchemaResponse = (response: any): any => {
    // ... (keep the existing conversion logic from the original component)
    const result = response.result;
    
    // Check if result is an array of field definitions
    if (Array.isArray(result)) {
      // Generate a random 6-character tag
      const randomTag = Math.random().toString(36).substring(2, 8).toUpperCase();
      const objectName = `UserObject${randomTag}`;
      
      // Build a hierarchical structure from flat dot-notation fields
      const buildNestedStructure = (flatFields: any[]) => {
        const root: { [key: string]: any } = {};
        const fieldMap: { [key: string]: any } = {};
        
        // First, identify all unique paths including intermediate ones
        const allPaths = new Set<string>();
        flatFields.forEach((fieldDef) => {
          // Support both old 'field_name' and new 'name' format
          const fieldName = fieldDef.field_name || fieldDef.name;
          const parts = fieldName.split('.');
          // Add all intermediate paths
          for (let i = 1; i <= parts.length; i++) {
            allPaths.add(parts.slice(0, i).join('.'));
          }
        });
        
        // Create field objects for all paths
        Array.from(allPaths).forEach((path) => {
          const existingField = flatFields.find(f => (f.field_name || f.name) === path);
          if (existingField) {
            // Use existing field definition
            fieldMap[path] = {
              ...existingField,
              field_name: existingField.field_name || existingField.name,
              _children: {}
            };
          } else {
            // Create intermediate object field
            fieldMap[path] = {
              field_name: path,
              field_type: 'object',
              description: `${path.split('.').pop()?.replace(/_/g, ' ')} details`,
              is_required: flatFields.some(f => {
                const fName = f.field_name || f.name;
                return fName.startsWith(path + '.') && f.is_required;
              }),
              is_list: false,
              _children: {}
            };
          }
        });
        
        // Build hierarchy
        Object.keys(fieldMap).sort().forEach((path) => {
          const parts = path.split('.');
          const field = fieldMap[path];
          
          if (parts.length === 1) {
            // Top-level field
            root[path] = field;
          } else {
            // Find parent and add as child
            const parentPath = parts.slice(0, -1).join('.');
            const childName = parts[parts.length - 1];
            
            if (fieldMap[parentPath]) {
              fieldMap[parentPath]._children[childName] = field;
              
              // If parent is a list, remove this from root
              if (fieldMap[parentPath].is_list) {
                delete root[path];
              }
            }
          }
        });
        
        // Special handling for list fields with nested structure
        Object.values(fieldMap).forEach((field: any) => {
          if (field.is_list && field.field_type === 'object') {
            // Find all fields that start with this field's name + '.'
            const fieldName = field.field_name || field.name;
            const prefix = fieldName + '.';
            const childFields: { [key: string]: any } = {};
            
            Object.keys(fieldMap).forEach((path) => {
              if (path.startsWith(prefix)) {
                const childPath = path.substring(prefix.length);
                if (!childPath.includes('.')) {
                  // Direct child - remove it from root if it's there
                  delete root[path];
                  childFields[childPath] = fieldMap[path];
                }
              }
            });
            
            // Add child fields to the parent
            Object.assign(field._children, childFields);
          }
        });
        
        // Convert to array format
        const processField = (field: any, name: string): any => {
          const children = Object.entries(field._children || {}).map(([childName, childField]: [string, any]) => 
            processField(childField, childName)
          );
          
          return {
            ...field,
            field_name: name,
            nested_fields: children.length > 0 ? children : undefined,
            _children: undefined
          };
        };
        
        // Filter out fields that are children of other fields
        const rootFields: { [key: string]: any } = {};
        Object.entries(root).forEach(([name, field]) => {
          // Check if this field is a child of another field
          let isChild = false;
          Object.keys(root).forEach((otherName) => {
            if (name.startsWith(otherName + '.') && name !== otherName) {
              isChild = true;
            }
          });
          
          if (!isChild) {
            rootFields[name] = field;
          }
        });
        
        return Object.entries(rootFields).map(([name, field]) => processField(field, name));
      };
      
      const processedFields = buildNestedStructure(result);
      
      const fields = processedFields.map((fieldDef: any) => {
        // Support both old 'field_name' and new 'name' format
        const fieldName = fieldDef.field_name || fieldDef.name;
        const displayName = fieldDef.display_name || fieldName.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
        
        const field: Field = {
          id: `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          name: fieldName,
          display_name: displayName,
          field_type: mapFieldTypeFromAPI(fieldDef.field_type, fieldDef.is_list),
          description: fieldDef.description || '',
          is_required: fieldDef.is_required || false,
          is_unique: fieldName === 'id' || fieldName.includes('_id'),
          is_indexed: fieldDef.field_type === 'string' || fieldName.includes('_id'),
          help_text: fieldDef.description || '',
          placeholder: '',
        };
        
        if (fieldDef.constraints) {
          if (fieldDef.constraints.min !== undefined) field.min_value = fieldDef.constraints.min;
          if (fieldDef.constraints.max !== undefined) field.max_value = fieldDef.constraints.max;
          if (fieldDef.constraints.min_length !== undefined) field.min_length = fieldDef.constraints.min_length;
          if (fieldDef.constraints.max_length !== undefined) field.max_length = fieldDef.constraints.max_length;
        }
        
        if (fieldDef.default_value !== null && fieldDef.default_value !== undefined) {
          field.default_value = fieldDef.default_value;
        }
        
        if (fieldDef.is_list || fieldDef.field_type === 'array') {
          field.field_type = FieldType.ARRAY;
          // Try to infer array item type from field metadata
          if (fieldDef.array_item_type) {
            field.array_item_type = mapFieldTypeFromAPI(fieldDef.array_item_type, false);
          } else if (fieldDef.field_type === 'object') {
            // This is a list of objects
            field.array_item_type = FieldType.JSON;
            // Don't process nested fields here, they'll be handled below
          } else {
            field.array_item_type = FieldType.STRING;
          }
        }
        
        // Handle nested fields recursively
        const mapNestedField = (nestedDef: any): Field => {
          // Support both old 'field_name' and new 'name' format
          const nestedFieldName = nestedDef.field_name || nestedDef.name;
          const nestedDisplayName = nestedDef.display_name || nestedFieldName.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
          
          const nestedField: Field = {
            id: `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            name: nestedFieldName,
            display_name: nestedDisplayName,
            field_type: mapFieldTypeFromAPI(nestedDef.field_type, nestedDef.is_list),
            description: nestedDef.description || '',
            is_required: nestedDef.is_required || false,
            is_unique: false,
            is_indexed: false,
            help_text: nestedDef.description || '',
            placeholder: '',
          };
          
          // Handle array item types
          if (nestedDef.is_list && nestedDef.field_type === 'object') {
            nestedField.field_type = FieldType.ARRAY;
            nestedField.array_item_type = FieldType.JSON;
          } else if (nestedDef.is_list) {
            nestedField.field_type = FieldType.ARRAY;
            nestedField.array_item_type = mapFieldTypeFromAPI(nestedDef.field_type, false);
          }
          
          // Recursively handle nested fields
          if (nestedDef.nested_fields && nestedDef.nested_fields.length > 0) {
            nestedField.nested_fields = nestedDef.nested_fields.map(mapNestedField);
          }
          
          return nestedField;
        };
        
        // Apply nested fields for both regular objects and arrays of objects
        if (fieldDef.nested_fields && fieldDef.nested_fields.length > 0) {
          field.nested_fields = fieldDef.nested_fields.map(mapNestedField);
        }
        
        return field;
      });
      
      return {
        name: objectName,
        display_name: objectName.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()),
        description: quickStartInput,
        is_searchable: true,
        fields: fields
      };
    }
    
    // Otherwise, handle as JSON schema format
    let objectSchema = result;
    if (result.type === 'array' && result.items) {
      objectSchema = result.items;
    }
    
    if (!objectSchema.properties) {
      throw new Error('No properties found in schema');
    }
    
    const description = result.description || 'Custom Object';
    // Generate a random 6-character tag
    const randomTag = Math.random().toString(36).substring(2, 8).toUpperCase();
    const objectName = `UserObject${randomTag}`;
    
    const fields: any[] = [];
    const requiredFields = objectSchema.required || [];
    
    for (const [propName, propDef] of Object.entries(objectSchema.properties as Record<string, any>)) {
      const field: any = {
        name: propName,
        display_name: propName.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()),
        field_type: mapJsonTypeToFieldType(propDef.type, propDef),
        description: propDef.description || '',
        is_required: requiredFields.includes(propName),
        is_unique: propName === 'id' || propName.includes('_id'),
        is_indexed: propDef.type === 'string' || propName.includes('_id'),
      };
      
      if (propDef.type === 'string') {
        if (propDef.minLength) field.min_length = propDef.minLength;
        if (propDef.maxLength) field.max_length = propDef.maxLength;
        if (propDef.enum) field.enum_values = propDef.enum;
      }
      
      if (propDef.type === 'object' && propDef.properties) {
        field.field_type = FieldType.JSON;
        field.help_text = `JSON object with properties: ${Object.keys(propDef.properties).join(', ')}`;
      }
      
      fields.push(field);
    }
    
    return {
      name: objectName,
      display_name: objectName.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()),
      description: description,
      is_searchable: true,
      fields: fields
    };
  };

  const mapFieldTypeFromAPI = (apiType: string, isList: boolean): FieldType => {
    if (isList) return FieldType.ARRAY;
    
    switch (apiType.toLowerCase()) {
      case 'string': return FieldType.STRING;
      case 'number': return FieldType.FLOAT;
      case 'integer': return FieldType.INTEGER;
      case 'boolean': return FieldType.BOOLEAN;
      case 'date': return FieldType.DATE;
      case 'datetime': return FieldType.DATETIME;
      case 'json': return FieldType.JSON;
      case 'text': return FieldType.TEXT;
      case 'uuid': return FieldType.UUID;
      case 'array': return FieldType.ARRAY;
      case 'object': return FieldType.JSON;
      default: return FieldType.STRING;
    }
  };

  const mapJsonTypeToFieldType = (jsonType: string, propDef: any): FieldType => {
    if (propDef.enum && propDef.enum.length > 0) {
      return FieldType.STRING;
    }
    if (propDef.format === 'date') return FieldType.DATE;
    if (propDef.format === 'date-time') return FieldType.DATETIME;
    if (propDef.format === 'uuid') return FieldType.UUID;
    
    switch (jsonType) {
      case 'string': 
        if (propDef.maxLength && propDef.maxLength > 255) {
          return FieldType.TEXT;
        }
        return FieldType.STRING;
      case 'integer': return FieldType.INTEGER;
      case 'number': return FieldType.FLOAT;
      case 'boolean': return FieldType.BOOLEAN;
      case 'array': return FieldType.ARRAY;
      case 'object': return FieldType.JSON;
      default: return FieldType.STRING;
    }
  };

  const handleQuickStartObject = async () => {
    if (!quickStartInput.trim()) {
      setSnackbar({ open: true, message: 'Please describe what object you want to create', severity: 'warning' });
      return;
    }

    setQuickStartLoading(true);
    try {
      const idToken = await user?.getIdToken();
      
      const response = await fetch('/api/v2/functions/public/predictobjectforfunction', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          input: quickStartInput,
          input_type: quickStartInputType,
          model: DEFAULT_ALLOWED_MODEL_FOR_PUBLIC_FUNCTIONS[0]
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate object schema');
      }

      const data = await response.json();
      
      // Handle both wrapped and direct responses
      let responseData = null;
      if (data.result) {
        // Direct result
        responseData = data.result;
      } else if (data.success && data.data) {
        // Wrapped response
        responseData = data.data;
      } else if (data.success && data.result) {
        // Legacy wrapped response
        responseData = data.result;
      } else if (data.data) {
        // Alternative wrapped response
        responseData = data.data;
      } else {
        // Assume data itself is the response
        responseData = data;
      }
      
      if (responseData) {
        let processedFields = [];
        let objectName = '';
        let displayName = '';
        let description = quickStartInput;
        
        // Check if this is wrapped in return_object (from function generator)
        let objectSchema = null;
        if (responseData.return_object) {
          objectSchema = responseData.return_object;
        } else {
          objectSchema = responseData;
        }
        
        // Check if this is the new AIObjectReturnWrapper format
        if (objectSchema.name && objectSchema.display_name && objectSchema.fields) {
          // New format with object metadata
          objectName = objectSchema.name;
          displayName = objectSchema.display_name;
          description = objectSchema.description || quickStartInput;
          
          // Process the fields array - if fields are already formatted, use them directly
          if (Array.isArray(objectSchema.fields) && objectSchema.fields.length > 0) {
            const firstField = objectSchema.fields[0];
            // Check if fields are already in the new format (have field_name, field_type, etc.)
            if (firstField.field_type && (firstField.field_name || firstField.name)) {
              // Fields are already in the correct format, use them directly
              processedFields = objectSchema.fields;
            } else {
              // Fields need conversion from old format
              const convertedObject = convertSchemaResponse({ result: objectSchema.fields });
              processedFields = convertedObject.fields || [];
            }
          }
        } else {
          // Old format - array of fields
          const convertedObject = convertSchemaResponse({ result: objectSchema });
          objectName = convertedObject.name || '';
          displayName = convertedObject.display_name || convertedObject.name || '';
          description = convertedObject.description || quickStartInput;
          processedFields = convertedObject.fields || [];
        }
        
        // Set the object with metadata
        setObject({
          name: objectName,
          display_name: displayName,
          description: description,
          is_searchable: true,
          fields: []
        });
        
        if (processedFields && Array.isArray(processedFields)) {
          const importedFields = processedFields.map((field: any, index: number) => ({
            id: field.id || `field_${Date.now()}_${index}`,
            name: field.name || field.field_name || '',
            display_name: field.display_name || field.name || field.field_name || '',
            field_type: field.field_type || FieldType.STRING,
            description: field.description || '',
            is_required: field.is_required || false,
            is_unique: field.is_unique || false,
            is_indexed: field.is_indexed || false,
            help_text: field.help_text || field.description || '',
            placeholder: field.placeholder || '',
            min_length: field.min_length,
            max_length: field.max_length,
            min_value: field.min_value,
            max_value: field.max_value,
            regex_pattern: field.regex_pattern,
            allowed_values: field.allowed_values || field.enum_values,
            default_value: field.default_value,
            ui_widget: field.ui_widget,
            reference_object_id: field.reference_object_id,
            array_item_type: field.array_item_type,
            nested_fields: field.nested_fields  // IMPORTANT: Include nested fields!
          }));
          setFields(importedFields);
        }
        
        // Switch to manual mode to show the form
        setCreationMode('manual');
        setQuickStartInput('');
        setSnackbar({ open: true, message: 'Object schema generated successfully!', severity: 'success' });
      } else {

        throw new Error('Invalid response format from AI function');
      }
    } catch (error) {

      setSnackbar({ open: true, message: 'Failed to generate object schema', severity: 'error' });
    } finally {
      setQuickStartLoading(false);
    }
  };

  const resetEditor = () => {
    setObject({
      name: '',
      display_name: '',
      description: '',
      is_searchable: true,
      fields: [],
    });
    setFields([]);
    setCreationMode(null);
    setSelectedObjectId(null);
    setObjectVersions([]);
    setCurrentVersion(null);
    // Otherwise the ?edit= effect reopens the object whenever the list reloads.
    if (!isModal && initialObjectId) {
      navigate('/dashboard/objects', { replace: true });
    }
  };

  const hasUnsavedChanges = () =>
    selectedObjectId
      ? savedDefinitionRef.current?.key !== definitionKey()
      : Boolean(object.display_name || object.description || fields.length);

  const confirmDiscard = () =>
    !hasUnsavedChanges() || window.confirm('Discard your unsaved changes to this object?');

  const openGenerator = (params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params)}` : '';
    navigate(`/dashboard/objects/generate${query}`);
  };

  const canEditWithAI = (obj?: CustomObject) => isPublicMode || obj?.customer_id !== 'public';

  const leaveEditor = () => {
    if (confirmDiscard()) resetEditor();
  };

  const openEditorInGenerator = () => {
    if (selectedObjectId) {
      if (confirmDiscard()) openGenerator({ object: selectedObjectId });
      return;
    }
    if (fields.length > 0 && !window.confirm("The fields you've added won't be carried over to the Object Generator. Continue?")) {
      return;
    }
    const prompt = [object.display_name, object.description].map(s => s?.trim()).filter(Boolean).join(': ');
    openGenerator(prompt ? { prompt } : undefined);
  };

  const startManual = () => {
    resetEditor();
    setCreationMode('manual');
  };

  const editObject = (objectId: string) => {
    handleSelectObject(objectId);
    setCreationMode('manual');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const idToken = await user?.getIdToken();
      
      // Check if we're updating an existing object
      if (selectedObjectId) {
        // Update existing object - creates new version
        const objectToUpdate = {
          name: object.name,
          display_name: object.display_name,
          description: object.description,
          is_searchable: object.is_searchable,
          fields: fields,
        };
        
        const response = await fetchWithPublicMode(`/api/v2/custom-objects/${selectedObjectId}?cascade_to_functions=${cascadeToFunctions}`, {
          method: 'PUT',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`
          },
          body: JSON.stringify(objectToUpdate),
        }, isPublicMode, permissions?.isAdmin || false);
        
        if (response.ok) {
          const savedData = await response.json();
          const savedObject = savedData?.data || savedData?.results || savedData;
          
          // Check if functions were cascaded
          const cascadedCount = savedObject?.cascaded_functions?.length || 0;
          const message = cascadedCount > 0 
            ? `Object updated successfully! (New version created, ${cascadedCount} function${cascadedCount === 1 ? '' : 's'} updated)`
            : 'Object updated successfully! (New version created)';
          
          setSnackbar({ open: true, message, severity: 'success' });
          setShowSaveDialog(false);
          setCascadeToFunctions(false);
          
          // Reload saved objects
          await loadSavedObjects();
          
          // Update the selected object ID to the new version
          if (savedObject?.id) {
            setSelectedObjectId(savedObject.id);
          }
          
          if (onSave && savedObject) {
            onSave(savedObject);
          }
          if (isModal && onClose) {
            onClose();
          }
        } else {
          const error = await response.text();
          throw new Error(`Failed to update object: ${error}`);
        }
      } else {
        // Create new object
        const baseName = generateFieldName(object.display_name);
        let objectSaved = false;
        let savedObject = null;
        let attemptCount = 0;
        const maxAttempts = 50;
        
        while (!objectSaved && attemptCount < maxAttempts) {
          const attemptName = attemptCount === 0 ? baseName : `${baseName}_${attemptCount + 1}`;
          
          const objectToSave = {
            ...object,
            name: attemptName,
            fields: fields,
          };
          
          const response = await fetchWithPublicMode('/api/v2/custom-objects/', {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${idToken}`
            },
            body: JSON.stringify(objectToSave),
          }, isPublicMode, permissions?.isAdmin || false);
          
          if (response.ok) {
            const savedData = await response.json();
            savedObject = savedData?.data || savedData?.results || savedData;
            objectSaved = true;
          } else {
            const error = await response.text();
            if (response.status === 400 && (error.includes('already exists') || error.includes('duplicate'))) {
              attemptCount++;
              continue;
            } else {
              throw new Error(`Failed to save object: ${error}`);
            }
          }
        }
        
        if (!objectSaved) {
          throw new Error('Failed to save object: unable to find unique name');
        }
        
        setSnackbar({ open: true, message: 'Object saved successfully!', severity: 'success' });
        setShowSaveDialog(false);
        
        if (onSave && savedObject) {
          onSave(savedObject);
        }
        if (isModal && onClose) {
          onClose();
        }
      }
      resetEditor();
      
      if (!isModal) {
        loadSavedObjects();
      }
    } catch (error) {
      setSnackbar({ open: true, message: error instanceof Error ? error.message : 'Failed to save object', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Filter objects based on search term
  const filteredObjects = savedObjects.filter(obj => {
    if (!tableSearchTerm) return true;
    const search = tableSearchTerm.toLowerCase();
    return (
      obj.display_name.toLowerCase().includes(search) ||
      obj.name.toLowerCase().includes(search) ||
      (obj.description && obj.description.toLowerCase().includes(search))
    );
  });

  // Sort filtered objects
  const sortedObjects = [...filteredObjects].sort((a, b) => {
    let aVal: any = a[tableSortBy as keyof CustomObject];
    let bVal: any = b[tableSortBy as keyof CustomObject];
    
    // Handle dates
    if (tableSortBy === 'created_at' || tableSortBy === 'updated_at') {
      aVal = new Date(aVal || 0).getTime();
      bVal = new Date(bVal || 0).getTime();
    }
    
    // Handle field_count
    if (tableSortBy === 'field_count') {
      aVal = a.field_count || a.fields?.length || 0;
      bVal = b.field_count || b.fields?.length || 0;
    }
    
    if (aVal < bVal) return tableSortDir === 'asc' ? -1 : 1;
    if (aVal > bVal) return tableSortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const handleTableSort = (column: string) => {
    if (tableSortBy === column) {
      setTableSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setTableSortBy(column);
      setTableSortDir('asc');
    }
    setTablePage(0);
  };

  const openDeleteModal = async (objectId: string, objectName: string) => {
    setObjectToDelete({ id: objectId, name: objectName });
    setDependentFunctions([]);
    
    try {
      const idToken = await user?.getIdToken();
      
      // Check if the object is in use
      const usageResponse = await fetch(`/api/v2/custom-objects/${objectId}/usage`, {
        headers: {
          'Authorization': `Bearer ${idToken}`,
        },
      });

      if (!usageResponse.ok) {
        throw new Error('Failed to check object usage');
      }

      const usageData = await usageResponse.json();
      const usage = usageData.data || usageData;

      if (usage.is_used && usage.functions) {
        // Convert functions to DependentItem format
        const deps = usage.functions.map((f: any) => ({
          id: f.id,
          name: f.name,
          description: f.description,
          type: 'function',
        }));
        setDependentFunctions(deps);
      }
    } catch (err) {

    }
    
    setDeleteModalOpen(true);
  };

  const handleDeleteObject = async () => {
    if (!objectToDelete) return;

    const idToken = await user?.getIdToken();
    const response = await fetch(`/api/v2/custom-objects/${objectToDelete.id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      // Check if it's a usage error (shouldn't happen as we checked above, but just in case)
      if (errorData.error === 'Object is in use') {
        const functionList = errorData.functions.map((f: any) => `  • ${f.name}`).join('\n');
        throw new Error(
          `Cannot deactivate "${objectToDelete.name}" because it is used by ${errorData.functions.length} function(s):\n\n` +
          `${functionList}\n\n` +
          `Please remove this object from these functions before deactivating it.`
        );
      }
      throw new Error('Failed to deactivate object');
    }

    setSnackbar({ open: true, message: 'Object deactivated successfully!', severity: 'success' });
    setObjectToDelete(null);
    setDependentFunctions([]);
    await loadSavedObjects();
  };

  // Table columns definition
  const objectTableColumns: Column<CustomObject>[] = [
    {
      id: 'display_name',
      label: 'Name',
      sortable: true,
      render: (value, row) => <ObjectJsonTooltip objectId={row.id}>{value}</ObjectJsonTooltip>,
    },
    {
      id: 'name',
      label: 'Technical Name',
      sortable: true,
      render: (value) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
          {value}
        </Typography>
      ),
    },
    {
      id: 'description',
      label: 'Description',
      sortable: false,
      render: (value) => value || '-',
    },
    {
      id: 'usage',
      label: 'Used By',
      sortable: false,
      align: 'center',
      render: (_, row) => {
        const usage = row.id ? objectUsage[row.id] : null;
        if (!usage || usage.count === 0) {
          return (
            <Chip 
              label="0"
              size="small"
              variant="outlined"
              sx={{ minWidth: 32 }}
            />
          );
        }
        return (
          <Tooltip 
            title={
              <Box sx={{ p: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                  Functions using this object:
                </Typography>
                {usage.functions.map((func) => (
                  <Box key={func.id} sx={{ display: 'flex', alignItems: 'center', py: 0.25 }}>
                    <Typography variant="caption" sx={{ mr: 1 }}>
                      • {func.name}
                    </Typography>
                    <Link
                      href={`/dashboard/ai-functions?id=${func.id}`}
                      target="_blank"
                      rel="noopener"
                      sx={{ fontSize: '0.7rem', color: 'primary.light' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      View
                    </Link>
                  </Box>
                ))}
              </Box>
            }
            placement="left"
            arrow
          >
            <Chip 
              label={usage.count.toString()}
              size="small"
              color="primary"
              variant="filled"
              sx={{ cursor: 'pointer', minWidth: 32 }}
            />
          </Tooltip>
        );
      },
    },
    {
      id: 'versions',
      label: 'Versions',
      sortable: false,
      align: 'center',
      render: (_, row) => {
        const versionUsage = row.id ? objectVersionUsage[row.id] : null;
        const totalVersions = versionUsage?.total_versions || row.version_count || 1;
        
        return (
          <Tooltip
            title={
              versionUsage ? (
                <Box sx={{ p: 1, maxHeight: 400, overflowY: 'auto' }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                    Version History:
                  </Typography>
                  {versionUsage.versions.map((v: any) => (
                    <Box key={v.object_id} sx={{ mb: 1.5, pb: 1, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>
                        Version {v.version} {v.is_active && '(current)'}
                      </Typography>
                      {v.function_count > 0 ? (
                        <>
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'grey.400' }}>
                            Used by {v.function_count} function{v.function_count !== 1 ? 's' : ''}:
                          </Typography>
                          {v.functions.map((func: any) => (
                            <Box key={func.id} sx={{ display: 'flex', alignItems: 'center', py: 0.25, pl: 1 }}>
                              <Typography variant="caption" sx={{ mr: 1, fontSize: '0.7rem' }}>
                                • {func.name} (v{func.version})
                              </Typography>
                              <Link
                                href={`/dashboard/ai-functions?id=${func.id}`}
                                target="_blank"
                                rel="noopener"
                                sx={{ fontSize: '0.65rem', color: 'primary.light' }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                View
                              </Link>
                            </Box>
                          ))}
                        </>
                      ) : (
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'grey.500', fontStyle: 'italic' }}>
                          Not used by any functions
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              ) : (
                'Loading version info...'
              )
            }
            placement="left"
            arrow
          >
            <Chip
              label={totalVersions.toString()}
              size="small"
              color={totalVersions > 1 ? 'secondary' : 'default'}
              variant={totalVersions > 1 ? 'filled' : 'outlined'}
              sx={{ cursor: 'pointer', minWidth: 32 }}
              onMouseEnter={async () => {
                if (row.id && !objectVersionUsage[row.id] && row.customer_id !== 'public') {
                  try {
                    const idToken = await user?.getIdToken();
                    const response = await fetch(`/api/v2/custom-objects/${row.id}/version-usage`, {
                      headers: { 'Authorization': `Bearer ${idToken}` }
                    });
                    if (response.ok) {
                      const result = await response.json();
                      setObjectVersionUsage(prev => ({
                        ...prev,
                        [row.id!]: result.data
                      }));
                    }
                  } catch (error) {

                  }
                }
              }}
            />
          </Tooltip>
        );
      },
    },
    /* Hidden for now - all objects are searchable by default
    {
      id: 'is_searchable',
      label: 'Searchable',
      sortable: true,
      align: 'center',
      render: (value) => (
        <Chip 
          label={value ? 'Yes' : 'No'} 
          size="small"
          color={value ? 'success' : 'default'}
          variant="outlined"
        />
      ),
    },
    */
    {
      id: 'created_at',
      label: 'Created',
      sortable: true,
      render: (value) => value ? new Date(value).toLocaleDateString() : '-',
    },
    {
      id: 'actions',
      label: 'Actions',
      sortable: false,
      align: 'center',
      render: (_, row) => (
        <Stack direction="row" spacing={1} justifyContent="center">
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => editObject(row.id!)}>
              <EditIcon />
            </IconButton>
          </Tooltip>
          {canEditWithAI(row) && (
            <Tooltip title="Edit with AI">
              <IconButton
                size="small"
                color="primary"
                onClick={() => openGenerator({ object: row.id! })}
              >
                <AutoAwesomeIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Deactivate">
            <IconButton
              size="small"
              color="error"
              onClick={() => openDeleteModal(row.id!, row.display_name)}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  const isEditingExisting = Boolean(selectedObjectId);
  const editingEditable = canEditWithAI(savedObjects.find(o => o.id === selectedObjectId));

  const content = (
    <Box>
      {!isModal && !creationMode && (
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems="flex-start"
          sx={{ mb: 3 }}
        >
          <Box sx={{ maxWidth: 720 }}>
            <Typography variant="h4" gutterBottom>
              Data & Outputs
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Objects define the structured data your AI functions return: the fields, their types and which ones are required.
              Create an object here, then pick it as a function's output format.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.5} sx={{ flexShrink: 0 }}>
            <Button variant="outlined" startIcon={<EditIcon />} onClick={startManual}>
              Create manually
            </Button>
            <Button variant="contained" startIcon={<AutoAwesomeIcon />} onClick={() => openGenerator()}>
              Generate with AI
            </Button>
          </Stack>
        </Stack>
      )}

      {!isModal && creationMode && (
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'flex-end' }}
          sx={{ mb: 3 }}
        >
          <Box>
            <Button size="small" startIcon={<ArrowBackIcon />} onClick={leaveEditor} sx={{ mb: 1, ml: -0.5 }}>
              All objects
            </Button>
            <Typography variant="h4" gutterBottom>
              {isEditingExisting ? object.display_name || 'Edit object' : 'New object'}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {isEditingExisting
                ? 'Change the details or fields, then save. Saving creates a new version of the object.'
                : 'Name your object, add the fields it should contain, then save it.'}
            </Typography>
          </Box>
          {(!isEditingExisting || editingEditable) && (
            <Button
              variant="outlined"
              startIcon={<AutoAwesomeIcon />}
              onClick={openEditorInGenerator}
              sx={{ flexShrink: 0 }}
            >
              {isEditingExisting ? 'Edit with AI' : 'Generate with AI instead'}
            </Button>
          )}
        </Stack>
      )}

      {/* Data & Outputs list, or an empty state that points to the generator */}
      {!isModal && !creationMode && (objectsLoaded && savedObjects.length === 0 ? (
        <Paper variant="outlined" sx={{ p: { xs: 3, md: 6 }, textAlign: 'center' }}>
          <ObjectIcon color="primary" sx={{ fontSize: 48, mb: 1 }} />
          <Typography variant="h6" gutterBottom>
            No objects yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 520, mx: 'auto' }}>
            Describe the data you want back, or paste an example JSON response, and the Object Generator builds the
            object for you. You can review and refine it before saving.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center" alignItems="center">
            <Button variant="contained" startIcon={<AutoAwesomeIcon />} onClick={() => openGenerator()}>
              Generate with AI
            </Button>
            <Button onClick={startManual}>Or build it field by field</Button>
          </Stack>
        </Paper>
      ) : (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', md: 'center' }}
            sx={{ mb: 2 }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Typography variant="h6">Your objects</Typography>
              {objectsLoaded && <Chip label={savedObjects.length} size="small" />}
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                size="small"
                placeholder="Search objects..."
                value={tableSearchTerm}
                onChange={(e) => {
                  setTableSearchTerm(e.target.value);
                  setTablePage(0);
                }}
                sx={{ width: { xs: '100%', md: 300 } }}
              />
              <Tooltip title="Deactivate functions and objects that nothing uses">
                <Button
                  variant="outlined"
                  onClick={async () => {
                    try {
                      const idToken = await user?.getIdToken();
                      const preview = await fetch('/api/v2/ai-functions/cleanup-orphans?dry_run=true', {
                        method: 'POST', headers: { Authorization: `Bearer ${idToken}` },
                      });
                      if (!preview.ok) return;
                      const previewData = await preview.json();
                      if (previewData.total_functions === 0 && previewData.total_objects === 0) {
                        alert('Nothing to clean up — no orphaned items found.');
                        return;
                      }
                      const items = [
                        ...previewData.orphaned_functions.map((f: any) => `Function: ${f.display_name || f.name}`),
                        ...previewData.orphaned_objects.map((o: any) => `Object: ${o.display_name || o.name}`),
                      ];
                      const msg = `Found ${previewData.total_functions} unused function(s) and ${previewData.total_objects} unused object(s):\n\n${items.slice(0, 20).join('\n')}${items.length > 20 ? `\n...and ${items.length - 20} more` : ''}\n\nDeactivate all?`;
                      if (!window.confirm(msg)) return;
                      const res = await fetch('/api/v2/ai-functions/cleanup-orphans?dry_run=false', {
                        method: 'POST', headers: { Authorization: `Bearer ${idToken}` },
                      });
                      if (res.ok) loadSavedObjects();
                    } catch (e) { console.error('Cleanup failed:', e); }
                  }}
                  sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                  Clean up unused
                </Button>
              </Tooltip>
            </Stack>
          </Stack>
          <DataTable
            columns={objectTableColumns}
            data={sortedObjects.slice(tablePage * tableRowsPerPage, (tablePage + 1) * tableRowsPerPage)}
            loading={!objectsLoaded}
            page={tablePage}
            rowsPerPage={tableRowsPerPage}
            totalCount={sortedObjects.length}
            onPageChange={(_, p) => setTablePage(p)}
            onRowsPerPageChange={(e) => { setTableRowsPerPage(parseInt(e.target.value, 10)); setTablePage(0); }}
            sortBy={tableSortBy}
            sortDirection={tableSortDir}
            onSort={handleTableSort}
            rowKey="id"
            emptyMessage={tableSearchTerm ? 'No objects match your search' : 'No objects found'}
          />
        </Paper>
      ))}

      {/* In the function builder's modal, AI generation stays in place so the new object is returned to the function */}
      {isModal && !creationMode && (
        <>
          <Paper sx={{ p: 4, textAlign: 'center', backgroundColor: '#f8f9fa', mb: 3 }}>
            <Typography variant="h5" gutterBottom>
              How would you like to create your object?
            </Typography>
            <Typography variant="body1" color="textSecondary" sx={{ mb: 4 }}>
              Choose your preferred method to build a custom data structure
            </Typography>
            
            <Grid container spacing={3} justifyContent="center">
              <Grid item xs={12} md={6}>
                <Card 
                  sx={{ 
                    p: 4, 
                    cursor: hasActiveSubscription ? 'pointer' : 'pointer',
                    border: '2px solid transparent',
                    transition: 'all 0.3s ease',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    height: '100%',
                    minHeight: 250,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    opacity: 1,
                    position: 'relative',
                    overflow: 'hidden',
                    filter: hasActiveSubscription ? 'none' : 'saturate(0.4) brightness(0.9)',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 10px 30px rgba(102, 126, 234, 0.3)',
                      filter: hasActiveSubscription ? 'none' : 'saturate(0.5) brightness(0.95)',
                    }
                  }}
                  onClick={() => {
                    if (hasActiveSubscription) {
                      setCreationMode('ai');
                    } else {
                      navigate('/dashboard/subscription');
                    }
                  }}
                >
                  {!hasActiveSubscription && (
                    <Box sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'radial-gradient(circle at center, rgba(0,0,0,0.3), rgba(0,0,0,0.5))',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 5,
                    }}>
                      <Box sx={{ textAlign: 'center', color: 'white' }}>
                        <LockIcon sx={{ fontSize: 48, mb: 1, opacity: 0.9 }} />
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          Click to Subscribe
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  
                  <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <AutoAwesomeIcon sx={{ fontSize: 56, mb: 2 }} />
                    <Typography variant="h5" gutterBottom fontWeight="bold">
                      AI-Powered Creation
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.9, mb: 2 }}>
                      {hasActiveSubscription 
                        ? 'Describe what you want in plain English and let AI generate the structure for you'
                        : 'Requires an active subscription'}
                    </Typography>
                    {hasActiveSubscription ? (
                      <Chip 
                        label="RECOMMENDED" 
                        size="small" 
                        sx={{ 
                          backgroundColor: 'rgba(255,255,255,0.3)', 
                          color: 'white',
                          fontWeight: 'bold'
                        }} 
                      />
                    ) : (
                      <Chip 
                        label="LOCKED" 
                        size="small" 
                        sx={{ 
                          backgroundColor: 'rgba(0,0,0,0.3)', 
                          color: 'white',
                          fontWeight: 'bold'
                        }} 
                      />
                    )}
                  </Box>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card 
                  sx={{ 
                    p: 4, 
                    cursor: 'pointer',
                    border: '2px solid transparent',
                    transition: 'all 0.3s ease',
                    background: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)',
                    color: 'white',
                    height: '100%',
                    minHeight: 250,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 10px 30px rgba(33, 150, 243, 0.3)',
                    }
                  }}
                  onClick={() => setCreationMode('manual')}
                >
                  {/* Background decoration */}
                  <Box sx={{
                    position: 'absolute',
                    top: -40,
                    right: -40,
                    width: 120,
                    height: 120,
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.1)',
                  }} />
                  <Box sx={{
                    position: 'absolute',
                    bottom: -30,
                    left: -30,
                    width: 100,
                    height: 100,
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.08)',
                  }} />
                  
                  <EditIcon sx={{ fontSize: 56, mb: 2, position: 'relative', zIndex: 1 }} />
                  <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ position: 'relative', zIndex: 1 }}>
                    Manual Builder
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9, mb: 2, position: 'relative', zIndex: 1 }}>
                    Build your object field by field with full control over every detail
                  </Typography>
                  <Chip 
                    label="FULL CONTROL" 
                    size="small" 
                    sx={{ 
                      backgroundColor: 'rgba(255,255,255,0.3)', 
                      color: 'white',
                      fontWeight: 'bold',
                      position: 'relative',
                      zIndex: 1
                    }} 
                  />
                </Card>
              </Grid>
            </Grid>
          </Paper>
        </>
      )}

      {/* AI Quick Start - Show when AI mode is selected */}
      {creationMode === 'ai' && (
        <Card sx={{
          mb: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
          position: 'relative',
          overflow: 'hidden',
        }}>
          <Box sx={{
            position: 'absolute',
            top: -80,
            right: -80,
            width: 250,
            height: 250,
            borderRadius: '50%',
            background: 'radial-gradient(circle at center, rgba(255,255,255,0.1), transparent 70%)',
          }} />
          <Box sx={{
            position: 'absolute',
            bottom: -60,
            left: -60,
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: 'radial-gradient(circle at center, rgba(255,255,255,0.08), transparent 70%)',
          }} />
          <CardContent sx={{ p: 4, position: 'relative', zIndex: 1 }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <AutoAwesomeIcon sx={{ color: 'white', fontSize: 36 }} />
                <Typography variant="h5" sx={{ color: 'white', fontWeight: 700 }}>
                  AI Object Generator
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 400 }}>
                Describe your data structure and let AI create the schema for you
              </Typography>
            </Box>
            <Box sx={{ 
              backgroundColor: 'rgba(255,255,255,0.95)', 
              borderRadius: 2, 
              p: 3,
              backdropFilter: 'blur(10px)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
            }}>
              {/* Input Type Selector */}
              <Box sx={{ mb: 2 }}>
                <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Input Type</InputLabel>
                  <Select
                    value={quickStartInputType}
                    onChange={(e) => setQuickStartInputType(e.target.value as 'text' | 'image')}
                    label="Input Type"
                  >
                    <MenuItem value="text">Text Input</MenuItem>
                    <MenuItem value="image">Image Input</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField
                  fullWidth
                  size="medium"
                  placeholder={quickStartInputType === 'text' 
                    ? "Example: I want to track student homework with questions, answers, and correctness ratings"
                    : "Example: Extract product information from images including name, price, and description"}
                  value={quickStartInput}
                  onChange={(e) => setQuickStartInput(e.target.value)}
                  sx={{ 
                    backgroundColor: 'white',
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: '#667eea',
                        borderWidth: 2
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#667eea',
                        borderWidth: 2
                      }
                    }
                  }}
                />
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleQuickStartObject}
                  disabled={!quickStartInput || quickStartLoading}
                  startIcon={quickStartLoading ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
                  sx={{ 
                    px: 4,
                    backgroundColor: '#667eea',
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '1.1rem',
                    textTransform: 'none',
                    boxShadow: '0 4px 15px rgba(102,126,234,0.4)',
                    '&:hover': {
                      backgroundColor: '#5a6dd8',
                      boxShadow: '0 6px 20px rgba(102,126,234,0.6)',
                    },
                    '&:disabled': {
                      backgroundColor: 'rgba(0,0,0,0.12)',
                      color: 'rgba(0,0,0,0.26)'
                    }
                  }}
                >
                  {quickStartLoading ? 'Generating...' : 'Generate'}
                </Button>
              </Box>
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
                <Button 
                  variant="text" 
                  size="small"
                  onClick={() => {
                    setCreationMode('manual');
                    setQuickStartInput('');
                    setQuickStartInputType('text');
                  }}
                  sx={{ color: '#667eea' }}
                >
                  Switch to Manual Mode
                </Button>
                <Button 
                  variant="text" 
                  size="small"
                  onClick={() => {
                    resetEditor();
                    setQuickStartInput('');
                  }}
                  sx={{ color: '#667eea' }}
                >
                  Start Over
                </Button>
                <Tooltip title="Paste example JSON and refine the result. Opens in a new tab; the object isn't added to this function automatically.">
                  <Button
                    variant="text"
                    size="small"
                    endIcon={<OpenInNewIcon fontSize="small" />}
                    onClick={() => {
                      const prompt = quickStartInput.trim();
                      const query = prompt ? `?${new URLSearchParams({ prompt })}` : '';
                      window.open(`/dashboard/objects/generate${query}`, '_blank', 'noopener');
                    }}
                    sx={{ color: '#667eea' }}
                  >
                    Open full Object Generator
                  </Button>
                </Tooltip>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Object Form - Show when manual mode is selected or after AI generation */}
      {(creationMode === 'manual' || (creationMode === 'ai' && fields.length > 0)) && (
        <>
          {/* Step-by-step guidance; the page shows this in its header instead */}
          {isModal && (
            <Box sx={{ mb: 3 }}>
              <Paper sx={{ 
                p: 3, 
                backgroundColor: '#e3f2fd',
                border: '2px solid #1976d2',
                position: 'relative'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <InfoIcon sx={{ color: '#1976d2', fontSize: 32 }} />
                  <Box>
                    <Typography variant="h6" color="#1976d2">
                      Build Your Custom Object
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Follow these steps: 1) Name your object → 2) Add fields to define the structure → 3) Save your object
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Box>
          )}

          {/* Step 1: Object Details Form */}
          <Paper sx={{ p: 4, mb: 3, border: '1px solid #e0e0e0' }}>
            {/* Version Information */}
            {selectedObjectId && currentVersion && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Chip 
                  label={`Version ${currentVersion}`} 
                  color="primary" 
                  size="small" 
                />
                {objectVersions.length > 1 && (
                  <Select
                    size="small"
                    value={selectedObjectId}
                    onChange={(e) => {
                      const versionId = e.target.value;
                      if (versionId !== selectedObjectId) {
                        handleSelectObject(versionId);
                      }
                    }}
                    sx={{ minWidth: 200 }}
                  >
                    {objectVersions.map((ver) => (
                      <MenuItem key={ver.id} value={ver.id}>
                        v{ver.version} - {new Date(ver.created_at).toLocaleDateString()} 
                        {ver.is_active && ' (active)'}
                      </MenuItem>
                    ))}
                  </Select>
                )}
                {objectVersions.length > 1 && (
                  <Typography variant="caption" color="text.secondary">
                    {objectVersions.length} versions total
                  </Typography>
                )}
              </Box>
            )}
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Box sx={{ 
                width: 40, 
                height: 40, 
                borderRadius: '50%', 
                backgroundColor: '#1976d2', 
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold'
              }}>
                1
              </Box>
              <Typography variant="h6">
                Name Your Object
              </Typography>
            </Box>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Object Name"
                  placeholder="e.g., Customer Profile"
                  value={object.display_name}
                  onChange={(e) => setObject({ 
                    ...object, 
                    display_name: e.target.value,
                    name: generateFieldName(e.target.value),
                  })}
                  helperText={object.name && `Will be saved as: ${object.name}`}
                  InputProps={{
                    startAdornment: <ObjectIcon sx={{ mr: 1, color: 'action.active' }} />,
                  }}
                />
              </Grid>
              
              {/* Hidden for now - all objects are searchable by default
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={object.is_searchable}
                      onChange={(e) => setObject({ ...object, is_searchable: e.target.checked })}
                    />
                  }
                  label="Make searchable by AI"
                />
              </Grid>
              */}
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Description"
                  placeholder="Describe what this object represents and when it should be used..."
                  value={object.description}
                  onChange={(e) => setObject({ ...object, description: e.target.value })}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Step 2: Fields Section */}
          <Paper sx={{ p: 4, border: '1px solid #e0e0e0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Box sx={{ 
                width: 40, 
                height: 40, 
                borderRadius: '50%', 
                backgroundColor: '#1976d2', 
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold'
              }}>
                2
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6">
                  Define Your Fields
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Add fields to define what data your object will store
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                {creationMode === 'ai' && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={resetEditor}
                  >
                    Start Over
                  </Button>
                )}
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={addField}
                  size="large"
                >
                  Add Field
                </Button>
              </Box>
            </Box>

            {fields.length === 0 ? (
              <Box sx={{ 
                p: 4, 
                backgroundColor: '#f5f5f5', 
                borderRadius: 2,
                textAlign: 'center' 
              }}>
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  No fields added yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Click "Add Field" above to start defining your object's structure
                </Typography>
              </Box>
            ) : (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {fields.length} field{fields.length !== 1 ? 's' : ''} defined
                </Typography>
                {fields.map((field, index) => renderFieldBuilder(field, index))}
              </Box>
            )}

            {/* Step 3: Save Actions */}
            <Box sx={{ 
              mt: 4, 
              pt: 3, 
              borderTop: '1px solid #e0e0e0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ 
                  width: 32, 
                  height: 32, 
                  borderRadius: '50%', 
                  backgroundColor: fields.length > 0 && object.display_name ? '#4caf50' : '#e0e0e0', 
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  fontSize: '0.875rem'
                }}>
                  3
                </Box>
                <Typography variant="body2" color={fields.length > 0 && object.display_name ? 'text.primary' : 'text.disabled'}>
                  Save your object when ready
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<CodeIcon />}
                  onClick={() => setShowPreview(true)}
                  disabled={fields.length === 0}
                >
                  Preview Code
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={() => setShowSaveDialog(true)}
                  disabled={!object.display_name || fields.length === 0}
                  sx={{ 
                    backgroundColor: '#4caf50',
                    '&:hover': {
                      backgroundColor: '#45a049'
                    }
                  }}
                  size="large"
                >
                  Save Object
                </Button>
              </Box>
            </Box>
          </Paper>
        </>
      )}

      {/* Preview Dialog */}
      <Dialog open={showPreview} onClose={() => setShowPreview(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Schema Preview</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {showPreview && (
            <DraftObjectSchemaView
              object={{ ...object, id: selectedObjectId || undefined, fields }}
              objects={savedObjects}
              unsaved={
                !selectedObjectId ||
                savedDefinitionRef.current?.id !== selectedObjectId ||
                savedDefinitionRef.current?.key !== definitionKey()
              }
              name={object.name || object.display_name}
              title={object.display_name || 'Untitled object'}
              maxHeight={480}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPreview(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Save Confirmation Dialog */}
      <Dialog open={showSaveDialog} onClose={() => setShowSaveDialog(false)}>
        <DialogTitle>Save Object</DialogTitle>
        <DialogContent>
          <Typography>
            {selectedObjectId 
              ? `Save changes to "${object.display_name}"? This will create a new version of the object.`
              : `Save the "${object.display_name}" object? This will make it available for AI functions to use.`}
          </Typography>
          {selectedObjectId && (
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={cascadeToFunctions}
                    onChange={(e) => setCascadeToFunctions(e.target.checked)}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">
                      Update all functions using this object
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      This will create new versions of all functions that currently use this object, 
                      updating them to use the new version automatically.
                    </Typography>
                  </Box>
                }
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowSaveDialog(false);
            setCascadeToFunctions(false);
          }}>Cancel</Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            color="primary"
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Object'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Delete Confirmation Modal */}
      {objectToDelete && (
        <DeleteConfirmationModal
          open={deleteModalOpen}
          onClose={() => {
            setDeleteModalOpen(false);
            setObjectToDelete(null);
            setDependentFunctions([]);
          }}
          onConfirm={handleDeleteObject}
          title="Deactivate Object"
          itemName={objectToDelete.name}
          itemType="object"
          dependentItems={dependentFunctions}
          isDeactivation={true}
        />
      )}
    </Box>
  );

  // If modal mode, wrap in a dialog
  if (isModal) {
    return (
      <Dialog 
        open={true} 
        onClose={onClose} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: { height: '90vh', display: 'flex', flexDirection: 'column' }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ObjectIcon /> Create New Object
            </Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ flex: 1, overflow: 'auto' }}>
          {content}
        </DialogContent>
      </Dialog>
    );
  }

  return content;
};
